import React, { useEffect } from "react";

// Shared UI building blocks used across pages so behaviour and look stay consistent.

export function Modal({
  open = true,
  onClose,
  title,
  subtitle,
  icon,
  size = "",
  children,
  footer,
  closeOnOverlay = true,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && onClose) onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="modal-overlay"
      onMouseDown={(e) => {
        if (closeOnOverlay && e.target === e.currentTarget && onClose) onClose();
      }}
    >
      <div className={`modal ${size ? `modal-${size}` : ""}`} role="dialog" aria-modal="true">
        <div className="modal-header">
          <div className="modal-title">
            {icon && (
              <span className="modal-title-icon">
                <i className={icon}></i>
              </span>
            )}
            <div>
              <h3>{title}</h3>
              {subtitle && <p>{subtitle}</p>}
            </div>
          </div>
          {onClose && (
            <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
              <i className="bi bi-x"></i>
            </button>
          )}
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

export function LoadingState({ message = "Loading..." }) {
  return (
    <div className="loading-state">
      <div className="spinner"></div>
      <p>{message}</p>
    </div>
  );
}

export function EmptyState({ icon = "bi bi-inbox", title, message, action }) {
  return (
    <div className="empty-state">
      <div className="empty-icon">
        <i className={icon}></i>
      </div>
      {title && <h3>{title}</h3>}
      {message && <p>{message}</p>}
      {action}
    </div>
  );
}

export function ErrorAlert({ message, onRetry, onDismiss }) {
  if (!message) return null;
  return (
    <div className="alert alert-danger" role="alert">
      <i className="bi bi-exclamation-circle"></i>
      <div className="alert-body">{message}</div>
      <div className="alert-actions">
        {onRetry && (
          <button type="button" className="btn-secondary btn-sm" onClick={onRetry}>
            <i className="bi bi-arrow-clockwise"></i> Retry
          </button>
        )}
        {onDismiss && (
          <button type="button" className="btn-ghost btn-icon btn-sm" onClick={onDismiss} aria-label="Dismiss">
            <i className="bi bi-x-lg"></i>
          </button>
        )}
      </div>
    </div>
  );
}

export function SearchField({ value, onChange, placeholder = "Search..." }) {
  return (
    <div className="search-field">
      <i className="bi bi-search"></i>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
      />
      {value && (
        <button type="button" className="search-clear" onClick={() => onChange("")} aria-label="Clear search">
          <i className="bi bi-x-circle-fill"></i>
        </button>
      )}
    </div>
  );
}

export function StatCard({ icon, tone = "brand", value, label, hint }) {
  return (
    <div className="stat-card">
      <div className={`stat-icon tone-${tone}`}>
        <i className={icon}></i>
      </div>
      <div className="stat-content">
        <h3>{value}</h3>
        <p>{label}</p>
        {hint && <small>{hint}</small>}
      </div>
    </div>
  );
}

// Returns page numbers with "..." gaps, e.g. [1, "...", 4, 5, 6, "...", 12]
const getPageItems = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const items = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) items.push("start-gap");
  for (let p = start; p <= end; p += 1) items.push(p);
  if (end < total - 1) items.push("end-gap");
  items.push(total);
  return items;
};

export function Pagination({ currentPage, totalPages, onPageChange, info }) {
  if (!totalPages || totalPages <= 1) {
    return info ? (
      <div className="pagination">
        <span className="pagination-info">{info}</span>
      </div>
    ) : null;
  }

  return (
    <div className="pagination">
      {info && <span className="pagination-info">{info}</span>}
      <div className="pagination-controls">
        <button
          type="button"
          className="page-btn"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          aria-label="Previous page"
        >
          <i className="bi bi-chevron-left"></i>
        </button>
        {getPageItems(currentPage, totalPages).map((item) =>
          typeof item === "number" ? (
            <button
              type="button"
              key={item}
              className={`page-btn ${item === currentPage ? "active" : ""}`}
              onClick={() => item !== currentPage && onPageChange(item)}
            >
              {item}
            </button>
          ) : (
            <span key={item} className="page-ellipsis">
              …
            </span>
          )
        )}
        <button
          type="button"
          className="page-btn"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          aria-label="Next page"
        >
          <i className="bi bi-chevron-right"></i>
        </button>
      </div>
    </div>
  );
}
